import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import SmartPagination from '@/components/SmartPagination';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Search, RefreshCw, Activity, User, Clock, MapPin, Tag, FileText, Filter, ShoppingCart, Package, UserCheck, LogIn, FolderOpen, Settings, Star, Store, UtensilsCrossed, Wrench, CreditCard, Link2, Crown } from 'lucide-react';

const entityIcons = {
  auth: LogIn,
  product: Package,
  order: ShoppingCart,
  seller: UserCheck,
  category: FolderOpen,
  user: User,
  settings: Settings,
  review: Star,
  supermarket: Store,
  restaurant: UtensilsCrossed,
  service_center: Wrench,
  refund: CreditCard,
  marketplace: Link2,
  membership: Crown,
  page: FileText,
};

const entityColors = {
  auth: 'bg-blue-50 text-blue-700',
  product: 'bg-emerald-50 text-emerald-700',
  order: 'bg-purple-50 text-purple-700',
  seller: 'bg-amber-50 text-amber-700',
  category: 'bg-teal-50 text-teal-700',
  user: 'bg-indigo-50 text-indigo-700',
  settings: 'bg-slate-50 text-slate-700',
  review: 'bg-yellow-50 text-yellow-700',
  supermarket: 'bg-lime-50 text-lime-700',
  restaurant: 'bg-orange-50 text-orange-700',
  service_center: 'bg-cyan-50 text-cyan-700',
  refund: 'bg-red-50 text-red-700',
  marketplace: 'bg-violet-50 text-violet-700',
  membership: 'bg-fuchsia-50 text-fuchsia-700',
  page: 'bg-sky-50 text-sky-700',
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ActivityLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const pageSize = 40;

  const fetchLogs = useCallback(async (isRefresh) => {
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      const params = { page, limit: pageSize };
      if (typeFilter) params.entity_type = typeFilter;
      if (search) params.search = search;

      const res = await api.get('/admin/activity-logs', { params });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Etkinlik gunlugu yuklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, typeFilter, search]);

  useEffect(() => { fetchLogs(false); }, [fetchLogs]);

  useEffect(() => {
    api.get('/admin/activity-logs/entity-types')
      .then(res => setEntityTypes(res.data || []))
      .catch(() => {});
  }, []);

  const handleSearch = (val) => { setSearch(val); setPage(1); };

  // Group logs by date
  const groupedLogs = logs.reduce((acc, log) => {
    const date = formatDate(log.created_at);
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="activity-log-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Etkinlik Gunlugu ({total})
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  placeholder="Ara..."
                  value={search}
                  onChange={e => handleSearch(e.target.value)}
                  className="pl-8 h-8 w-48 text-sm"
                  data-testid="activity-search-input"
                />
              </div>
              <select
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                className="h-8 text-sm border rounded-md px-2 bg-white"
                data-testid="activity-type-filter"
              >
                <option value="">Tum Islemler</option>
                {entityTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <Button size="sm" variant="outline" onClick={() => fetchLogs(true)} className="h-8" data-testid="activity-refresh-btn">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
              {(typeFilter || search) && (
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTypeFilter(''); setSearch(''); setPage(1); }}>
                  <Filter className="w-3 h-3 mr-1" /> Temizle
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-800" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              {search || typeFilter ? 'Filtre sonucu bulunamadi' : 'Henuz etkinlik kaydedilmemis'}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedLogs).map(([date, dateLogs]) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-xs font-semibold text-gray-500 bg-white px-2">{date}</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>
                  <div className="space-y-1">
                    {dateLogs.map((log, idx) => {
                      const Icon = entityIcons[log.entity_type] || Activity;
                      const colorClass = entityColors[log.entity_type] || 'bg-gray-50 text-gray-700';
                      return (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                          onClick={() => setSelectedLog(log)}
                          data-testid={`activity-row-${idx}`}
                        >
                          {/* Icon */}
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colorClass}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900">{log.action}</p>
                                {log.detail && (
                                  <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{log.detail}</p>
                                )}
                              </div>
                              <span className="text-[11px] text-gray-400 font-mono shrink-0 mt-0.5">{formatTime(log.created_at)}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              {log.user_email && (
                                <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                                  <User className="w-3 h-3" /> {log.user_email}
                                </span>
                              )}
                              {log.ip && log.ip !== 'unknown' && (
                                <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                                  <MapPin className="w-3 h-3" /> {log.ip}
                                </span>
                              )}
                              {log.entity_type && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${colorClass}`}>
                                  {log.entity_type}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <div className="pt-3 border-t mt-4">
              <SmartPagination currentPage={page} totalPages={totalPages} onPageChange={setPage} pageSize={pageSize} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-md" data-testid="activity-detail-modal">
          {selectedLog && (() => {
            const Icon = entityIcons[selectedLog.entity_type] || Activity;
            const colorClass = entityColors[selectedLog.entity_type] || 'bg-gray-50 text-gray-700';
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-base">{selectedLog.action}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  {selectedLog.detail && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Aciklama</p>
                      <p className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg">{selectedLog.detail}</p>
                    </div>
                  )}
                  <Separator />
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Tarih / Saat</p>
                      <p className="font-mono text-xs mt-0.5">{formatDateTime(selectedLog.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><Tag className="w-3 h-3" /> Islem Tipi</p>
                      <p className="mt-0.5"><span className={`text-xs px-2 py-0.5 rounded font-medium ${colorClass}`}>{selectedLog.entity_type || '-'}</span></p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><User className="w-3 h-3" /> Kullanici</p>
                      <p className="text-xs mt-0.5">{selectedLog.user_email || '-'}</p>
                      {selectedLog.user_id && (
                        <p className="text-[10px] text-gray-400 font-mono">{selectedLog.user_id.slice(0, 16)}...</p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> IP Adresi</p>
                      <p className="text-xs font-mono mt-0.5">{selectedLog.ip || '-'}</p>
                    </div>
                  </div>
                  {selectedLog.entity_id && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-xs text-gray-400 mb-1">Kayit ID</p>
                        <p className="text-xs font-mono bg-gray-50 p-2 rounded">{selectedLog.entity_id}</p>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityLogTab;
